import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, Fade, Paper, Skeleton, keyframes } from '@mui/material';
import { FunFactContext } from '../contexts';


const morphNumber = keyframes`
  0% {
    transform: scale(1) rotateY(0deg);
    opacity: 1;
  }
  50% {
    transform: scale(1.2) rotateY(90deg);
    opacity: 0.5;
  }
  100% {
    transform: scale(1) rotateY(0deg);
    opacity: 1;
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    text-shadow:
      0 0 5px rgba(255, 255, 255, 0.8),
      0 0 10px rgba(255, 255, 255, 0.6),
      0 0 15px rgba(255, 255, 255, 0.4);
  }
  50% {
    text-shadow:
      0 0 10px rgba(255, 255, 255, 1),
      0 0 20px rgba(255, 255, 255, 0.8),
      0 0 30px rgba(255, 255, 255, 0.6);
  }
`;

interface LoadingProps {
  isFront?: boolean;
  disableCountdown?: boolean;
  showFunFact?: boolean;
  message?: string;
}

export const AssertionCardLoading: FC<LoadingProps> = ({ isFront, disableCountdown = false, message, showFunFact = false  }) => {
  const initial = disableCountdown ? 0 : 5;
  const [countdown, setCountdown] = useState(initial);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayNumber, setDisplayNumber] = useState(5);
  const timerRef = useRef<NodeJS.Timeout>(null);
  const animationRef = useRef<NodeJS.Timeout>(null);

  // Reset state when isFront becomes false
  useEffect(() => {
    if (!isFront) {
      // Clear any running timers
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }

      // Reset all state
      setCountdown(5);
      setDisplayNumber(5);
      setIsAnimating(false);
    }
  }, [isFront]);

  useEffect(() => {
    // Only start the countdown when isFront is true
    if (!isFront || disableCountdown) return;

    timerRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          if (timerRef.current) {
            clearInterval(timerRef.current);
          }
          return 0;
        }

        // Start animation
        setIsAnimating(true);

        // Change the displayed number halfway through the animation
        animationRef.current = setTimeout(() => {
          setDisplayNumber(prev - 1);
        }, 200); // Half of animation duration

        // End animation
        setTimeout(() => {
          setIsAnimating(false);
        }, 400); // Full animation duration

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (animationRef.current) {
        clearTimeout(animationRef.current);
      }
    };
  }, [disableCountdown, isFront]);

  const resolvedMessage =
      message ??
      (countdown > 0 ? 'Get ready for the next statement...' : 'Coming up with the next statement...');

  return (
    <Box
      position="relative"
      variant="elevation"
      elevation={1}
      component={Paper}
      width="100%"
      height="100%"
      overflow="hidden"
      sx={{
        backgroundImage: 'linear-gradient(to right, #8e9eab, #eef2f3)',
        backgroundSize: '200% 200%',
        animation: 'GradientAnimation 3s ease infinite',
      }}
    >
      <Skeleton
        width="100%"
        height="100%"
        variant="rectangular"
        animation="wave"
      />
      <Fade in={isFront} timeout={600}>
        <Box
          position="absolute"
          width="100%"
          top="40%"
          sx={{
            transform: 'translateY(-50%)',
            zIndex: 2,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          px={3}
        >
          {!message && (
            <Typography variant="h1" textAlign="center" mb={3}>Did you know?</Typography>
          )}
          <Box className="speech-bubble">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={3}
              sx={{
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {countdown > 0 && (
                <Box
                  sx={{
                    animation: `${pulseGlow} 2s ease-in-out infinite`,
                    perspective: '1000px',
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: 72, md: 96 },
                      fontWeight: 900,
                      color: 'primary.main',
                      fontFamily: '\'Rubik\', sans-serif',
                      animation: isAnimating ? `${morphNumber} 0.4s cubic-bezier(0.4, 0, 0.2, 1)` : 'none',
                      transformStyle: 'preserve-3d',
                      lineHeight: 1,
                      transition: 'all 0.2s ease-out',
                    }}
                  >
                    {displayNumber}
                  </Typography>
                </Box>
              )}
              <Typography fontSize={countdown > 0 ? 18 : 24} component="span" variant="body1" align="center">
                {resolvedMessage}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

interface FunFactLoadingProps {
  assertionKey?: string;
}

export const FunFactLoading: FC<FunFactLoadingProps> = ({ assertionKey }) => {
  const { getNewFunFact } = useContext(FunFactContext);
  const resolvedMessage = useMemo(() => {
    const funFact = getNewFunFact(assertionKey);
    if (funFact) {
      return funFact.content;
    }
    return 'Coming up with the next statement...';
  }, [getNewFunFact, assertionKey]);

  return (
    <Box
      position="relative"
      variant="elevation"
      elevation={1}
      component={Paper}
      width="100%"
      height="100%"
      overflow="hidden"
      sx={{
        backgroundImage: 'linear-gradient(to right, #8e9eab, #eef2f3)',
        backgroundSize: '200% 200%',
        animation: 'GradientAnimation 3s ease infinite',
      }}
    >
      <Skeleton
        width="100%"
        height="100%"
        variant="rectangular"
        animation="wave"
      />
      <Fade in={true} timeout={600}>
        <Box
          position="absolute"
          width="100%"
          top="40%"
          sx={{
            transform: 'translateY(-50%)',
            zIndex: 2,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          px={3}
        >
          <Typography variant="h1" textAlign="center" mb={3}>Did you know?</Typography>
          <Box className="speech-bubble straight">
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={3}
              sx={{
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              <Typography fontSize={18} component="span" variant="body1" align="center">
                {resolvedMessage}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};
